<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import type { PropType } from 'vue'

const CLASS_NAME = {
  OPEN: 'is-open',
  CLOSE: 'is-close',
}

export default defineComponent({
  name: 'AccordionTrigger',

  props: {
    /**
     * アコーディオンの開閉状態管理
     *
     * @description true / false を受け取った場合は親で状態を管理する
     *              省略時、undefined または null の場合は initOpen を初期値として内部管理する
     */
    openState: {
      type: Boolean as PropType<Maybe<boolean>>,
      // Boolean prop の未指定時を false と区別し、内部管理モードとして扱うため undefined を明示する
      default: undefined,
    },
    /**
     * アコーディオンの開閉初期状態
     *
     * openState に boolean を受け取っている場合、本設定は無視される
     */
    initOpen: {
      type: Boolean,
      default: false,
    },
    /**
     * トグル可能かどうか
     */
    isTogglable: {
      type: Boolean,
      default: true,
    },
    /**
     * 本コンポーネントの root element のタグ名
     * dom に依存しないための属性
     *
     * @see https://jp.vuejs.org/v2/api/#is
     */
    tagName: {
      type: String,
      default: 'div',
    },
  },

  emits: ['update:openState'],

  setup(props, context) {
    /**
     * props で openState フラグが指定されなかった場合は
     * このコンポーネント内部のデータで状態を管理する
     */
    const openStateLocal = ref(props.initOpen)

    /**
     * props で openState の指定があれば、それをそのまま使う。
     * なければ、内部状態 openStateLocal を使う。
     */
    const isOpen = computed<boolean>(() => {
      return props.openState ?? openStateLocal.value
    })

    /**
     * 親から開閉状態（openState）を受け取っているかどうか
     * @return {boolean}
     */
    const hasGlobalState = computed<boolean>(() => {
      return props.openState != null
    })

    /**
     * トリガーを表示するかどうか
     * トグル機能なし、且つ展開状態になったらトリガーは非表示になる
     */
    const isShowTrigger = computed<boolean>(() => {
      return props.isTogglable || !isOpen.value
    })

    const toggle = () => {
      if (hasGlobalState.value) {
        context.emit('update:openState', !isOpen.value)
      } else {
        // 内部で更新後の状態を通知
        openStateLocal.value = !openStateLocal.value
        context.emit('update:openState', isOpen.value)
      }
    }

    return {
      CLASS_NAME,
      isOpen,
      isShowTrigger,
      toggle,
    }
  },
})
</script>

<template>
  <component
    :is="tagName"
    v-if="isShowTrigger"
    :class="[isOpen ? CLASS_NAME.OPEN : CLASS_NAME.CLOSE]"
    @click.prevent="toggle">
    <slot />
  </component>
</template>
